import React, { useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import SubscriptionService from '@/modules/base/components/subscription/subscriptionService'
import SubscriptionTypeSelectionV2 from './subscription-type-selection-v2'
import SubscriptionTypeSelection from './subscription-type-selection'
import { groupBy } from 'lodash'
import { ButtonSquare } from '@/modules/base/components/buttons'
import { updateSubscriptionDetails } from '@/redux/ducks/subscription/details'
import { isMeinBuero } from '@/utils/build-env'
import { subscriptionTypes } from '@/modules/base/util/constants'
import { useSelector } from 'react-redux'
import { navigate } from '@/modules/layout/util/useInterceptedNavigate'
import GtmService, { GtmEventTypes } from '@/modules/_services/gtm.service'
import { getPostLoginRedirectDecision, RedirectContext } from '@/utils/post-login-redirect'

const isMeinbuero = isMeinBuero()

const useStyles = makeStyles(theme => ({
  container: {
    padding: '20px 40px',
    [theme.devices.mobile]: {
      padding: 22,
    },
  },
  actionWrapper: {
    display: 'flex',
    justifyContent: 'center',
    boxShadow: '0 -3px 6px 0 rgba(0, 0, 0, 0.1)',
    padding: 20,

    [theme.devices.mobile]: {
      marginTop: 'auto',
      boxShadow: 'none',
    },
  },
  continueButton: {
    width: 175,
    [theme.devices.mobile]: {
      width: '100%',
    },
  },
}))

const SubscriptionTrialSelection = (props) => {
  const { onClose, context } = props
  const classes = useStyles({ isMeinbuero })
  const { subscriptions } = useSelector(state => state.subscription.details)

  const [groupedSubscriptionTypes, setGroupedSubscriptionTypes] = useState({})
  const [selectedSubscriptionTypes, setSelectedSubscriptionTypes] = useState([])

  useEffect(() => {
    if (isMeinbuero) {
      SubscriptionService.getProductsMbo().then(res => {
        setGroupedSubscriptionTypes(res.data)
      })
    } else {
      SubscriptionService.getWebSubscriptionTypes().then((body) => {
        const { data: subTypes } = body
        setGroupedSubscriptionTypes(groupBy(subTypes, 'type'))
      })
    }
  }, [])

  const onTypeButtonClicked = (types) => {
    setSelectedSubscriptionTypes(types)
  }

  const onConfirm = async() => {
    const effectiveSubscriptions = subscriptions.filter(sub =>
      !(isMeinbuero && sub.type === subscriptionTypes.FREE_2025),
    )
    const activeSubscriptions = effectiveSubscriptions.map(sb => sb.typeOfSubscription)
    const newSubscriptions = selectedSubscriptionTypes.filter(sb => !activeSubscriptions.includes(sb))
    const combinedSubscriptionTypes = Array.from(new Set([...activeSubscriptions, ...selectedSubscriptionTypes]))
    const hasDocumentTrialOnly = combinedSubscriptionTypes.length === 1 && combinedSubscriptionTypes[0] === 'document'
    const getRedirectTarget = () => {
      const decision = getPostLoginRedirectDecision({
        context: RedirectContext.POST_TRIAL,
        isMeinBuero: isMeinbuero,
        hasOnlyDocumentTrial: hasDocumentTrialOnly,
        postTrialFallbackPath: '/',
      })

      return decision.target || '/'
    }

    const isOnShop = typeof window !== 'undefined'
      && window.location?.pathname?.startsWith('/shop')

    if (newSubscriptions?.length) {
      SubscriptionService.createTrials(newSubscriptions)
        .then(() => {
          if (ENV.product === 'orgamax') {
            selectedSubscriptionTypes.forEach(type => {
              GtmService.pushEvent({
                event: GtmEventTypes.GaEventHub,
                eventCategory: 'Multi-Testauswahl',
                eventAction: 'Weiter',
                eventLabel: `Produktauswahl abgeschlossen ${type}`,
              })
            })
          }
          return updateSubscriptionDetails(true, true)
        })
        .then(() => onClose?.())
        .then(() => {
          if (isOnShop) return
          const targetPath = getRedirectTarget()
          navigate(targetPath)
        })
    } else {
      onClose?.()
      if (isOnShop) return
      const targetPath = getRedirectTarget()
      navigate(targetPath)
    }
  }
  return (
    <>
      <div className={classes.container}>
        {context === 'trialSelectNew'
          ? <SubscriptionTypeSelectionV2
            useBulletPointIcons={true}
            selectedSubscriptionTypes={selectedSubscriptionTypes}
            onChange={onTypeButtonClicked}
          />
          : <SubscriptionTypeSelection
            useBulletPointIcons={false}
            groupedSubscriptionTypes={groupedSubscriptionTypes}
            kind='multi'
            onChange={onTypeButtonClicked}
            context='trialSelect'
          />
        }
      </div>
      <div className={classes.actionWrapper}>
        <ButtonSquare
          customClass={classes.continueButton}
          label={lang.onboarding.general.finish}
          onClick={onConfirm}
          disabled={!selectedSubscriptionTypes.length && !subscriptions.length}
        />
      </div>
    </>
  )
}

export default SubscriptionTrialSelection
